/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { v3DialogConfig } from '@app/app/shared/helpers/dialog/v3-dialog-config';
import { AdminEnrolment } from '@app/app/shared/models/playlist/admin-enrolment.model';
import { ButtonSizes, ButtonTypes } from '@app/app/shared/styleguide/components/buttons/default/button.component';
import { StyleguideModule } from '@app/app/shared/styleguide/styleguide.module';
import { LucideChevronDown, LucideUpload, LucideUserPlus } from '@lucide/angular';
import { TranslocoModule } from '@ngneat/transloco';
import { EnrolCsvDialogComponent } from '../enrol-csv-dialog/enrol-csv-dialog.component';
import { EnrolDialogData, EnrolPersonDialogComponent } from '../enrol-person-dialog/enrol-person-dialog.component';

const CSV_DIALOG_WIDTH = '56rem';

/** The Enrol menu on a v3 Members page: enrol one person, or people from a CSV file. */
@Component({
  selector: 'ptl-v3-enrol-people-menu',
  templateUrl: './enrol-people-menu.component.html',
  styleUrls: ['./enrol-people-menu.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule, StyleguideModule],
})
export class EnrolPeopleMenuComponent {
  readonly playlistUid = input.required<string>();
  readonly contentTitle = input('');
  readonly published = input(true);
  readonly labelKey = input('translations.adminEnrolment.menu.enrolPeople');
  readonly buttonType = input<ButtonTypes>('primary');
  readonly size = input<ButtonSizes>('s');

  /** Someone was enrolled, so the members list is out of date. */
  readonly enrolled = output<void>();

  private readonly dialog = inject(MatDialog);

  protected readonly open = signal(false);
  protected readonly LucideChevronDown = LucideChevronDown;
  protected readonly LucideUpload = LucideUpload;
  protected readonly LucideUserPlus = LucideUserPlus;

  toggle(): void {
    this.open.update((open) => !open);
  }

  openPersonDialog(): void {
    this.open.set(false);
    this.dialog
      .open<EnrolPersonDialogComponent, EnrolDialogData, AdminEnrolment>(
        EnrolPersonDialogComponent,
        v3DialogConfig({ data: this.dialogData() }),
      )
      .afterClosed()
      .subscribe((enrolment) => {
        if (enrolment) {
          this.enrolled.emit();
        }
      });
  }

  openCsvDialog(): void {
    this.open.set(false);
    this.dialog
      .open<EnrolCsvDialogComponent, EnrolDialogData, boolean>(
        EnrolCsvDialogComponent,
        v3DialogConfig({ data: this.dialogData(), width: CSV_DIALOG_WIDTH, maxWidth: '95vw', disableClose: true }),
      )
      .afterClosed()
      .subscribe((enrolledAnyone) => {
        if (enrolledAnyone) {
          this.enrolled.emit();
        }
      });
  }

  private dialogData(): EnrolDialogData {
    return { playlistUid: this.playlistUid(), contentTitle: this.contentTitle(), published: this.published() };
  }
}
